import { useCallback, useState } from 'react';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { EditPasswordForm } from '@user/EditPasswordForm';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  isOpen: boolean;
  onClose(): void;
  mailAddress: string;
  groupId: string;
  invitationId: string;
  token: string | null;
};

export const UserSignUpWithEmailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  mailAddress,
  groupId,
  invitationId,
  token,
}: Props) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState('');

  const handleChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);
  const handleChangePassword = useCallback((password: string) => {
    setPassword(password);
  }, []);
  const isEmpty = password === '';

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);

    try {
      const response = await trpcClient.user.auth.signUpPasswordAuth.mutate({
        name,
        mailAddress,
        password,
        signUpMode: 'withInvitation',
        signUpRequestToken: null,
        invitation: { groupId, invitationId, token },
      });

      if (response.ok) {
        await FirebaseAuthenticationClient.signInWithCustomToken(response.token);
        onClose();
      } else {
        if (response.message === 'This email address is already registered.') {
          setError(
            'Balusにユーザ登録されているメールアドレスです。ユーザ登録フォームを閉じてからログインを選択して、ログインしてください。'
          );
        } else {
          setError('ユーザ登録に失敗しました。時間をあけてもう一度試してみてください。');
        }
      }
    } catch (error) {
      console.error(error);
      setError('ユーザ登録に失敗しました。時間をあけてもう一度試してみてください。');
    } finally {
      setProcessing(false);
    }
  }, [name, mailAddress, password, groupId, invitationId, token, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">ユーザ登録</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div className="pb-2 font-bold">メールアドレス (変更できません)</div>
        <input
          type="text"
          className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
          defaultValue={mailAddress}
          disabled
        />

        <div className="pb-2 pt-8 font-bold">名前</div>
        <input type="text" className="rounded-sm border p-2" value={name} onChange={handleChangeName} autoFocus />

        <EditPasswordForm onChange={handleChangePassword} disabled={processing} />

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>閉じる</FlatButton>
        <FlatButton
          color="brand"
          onClick={handleSubmit}
          className="ml-2 px-8"
          disabled={isEmpty || processing}
          loading={processing}
        >
          ユーザ登録
        </FlatButton>
      </div>
    </Modal>
  );
};
